import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Section } from '@components/ui/Section.jsx';
import { ConferenceTitleLockup } from '@components/home/ConferenceTitleLockup.jsx';
import { INTRODUCTION } from '@/data/homeContent.js';
import { BANGKOK } from '@/data/siteImagery.js';
import { OptimizedImage } from '@components/ui/OptimizedImage.jsx';

const GALLERY_IMAGES = [
  { src: '/gallery/2025/home/16.webp', alt: 'ISBM 2023 – Female speaker at conference' },
  { src: '/gallery/2025/home/6.webp', alt: 'ISBM 2024 – Male speaker at conference' },
  { src: '/gallery/2025/home/11.webp', alt: 'ISBM 2025 – Conference presentation' },
  { src: '/gallery/2025/home/14.webp', alt: 'ISBM 2025 – Speaker session' },
  { src: '/gallery/2023/home/4.webp', alt: 'ISBM 2025 – Keynote address' },
  { src: '/gallery/2024/home/4.webp',  alt: 'ISBM 2025 – Panel discussion' },
  { src: '/gallery/2024/home/8.webp', alt: 'ISBM 2025 – Research presentation' },
  { src: '/gallery/2024/home/11.webp', alt: 'ISBM 2024 – Conference highlight' },
];

export function IntroductionSection() {
  return (
    <Section className="relative overflow-hidden">
      <motion.div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.4] dark:opacity-20 bg-thai-pattern pointer-events-none"
      />
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          {/* Structured editorial title */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="eyebrow mb-6">{INTRODUCTION.eyebrow}</p>
            <ConferenceTitleLockup title={INTRODUCTION.title} />
          </motion.div>
          <div className="mt-8 max-w-3xl space-y-5">
            {INTRODUCTION.paragraphs.map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: i * 0.06, duration: 0.45 }}
                className="text-ink-600 dark:text-ink-300 leading-relaxed text-pretty"
              >
                {p}
              </motion.p>
            ))}
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/call-for-papers#dates" className="btn-primary inline-flex">
                Important Dates <ArrowUpRight size={16} />
              </Link>
              <Link to="/call-for-papers" className="btn-outline inline-flex">
                Call for Papers <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* Photo grid — 2 columns × 3 rows */}
        <div className="lg:col-span-5 lg:sticky lg:top-15">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-3xl ring-1 ring-ink-200/70 dark:ring-ink-700/50 p-2.5 shadow-[0_4px_24px_-6px_rgba(0,0,0,0.10)] dark:shadow-[0_4px_24px_-6px_rgba(0,0,0,0.35)]"
          >
            {/* Bangkok skyline card background */}
            <div aria-hidden className="absolute inset-0 pointer-events-none">
              <OptimizedImage
                src={BANGKOK.skyline.src}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-center scale-105"
                deferUntilVisible
              />
              <div className="absolute inset-0 bg-gradient-to-b from-white/55 via-white/40 to-white/30 dark:from-ink-950/65 dark:via-ink-950/45 dark:to-ink-950/35" />
            </div>

            <div className="relative z-10">
              <div className="grid grid-cols-2 gap-2">
                {GALLERY_IMAGES.map((img, i) => {
                  const isBottomRow = i >= GALLERY_IMAGES.length - 2;
                  const isBottomLeft = i === GALLERY_IMAGES.length - 2;
                  const isBottomRight = i === GALLERY_IMAGES.length - 1;

                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.15 }}
                      transition={{ delay: i * 0.06, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                      className="group relative overflow-hidden rounded-xl ring-1 ring-white/50 dark:ring-ink-700/50 shadow-sm"
                    >
                      <OptimizedImage
                        src={img.src}
                        alt={img.alt}
                        className="w-full aspect-[4/3] object-cover object-center transition-transform duration-700 ease-out will-change-transform group-hover:scale-[1.04]"
                        deferUntilVisible
                      />
                      {!isBottomRow && (
                        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      )}
                      {isBottomRow && (
                        <div className="absolute inset-x-0 bottom-0 pt-10 pb-2.5 px-3 bg-gradient-to-t from-ink-950/80 via-ink-950/45 to-transparent pointer-events-none">
                          {isBottomLeft && (
                            <p className="text-[11px] font-bold tracking-[0.22em] text-white uppercase">
                              ISBM
                            </p>
                          )}
                          {isBottomRight && (
                            <p className="text-[11px] tracking-[0.16em] text-white/90 font-medium text-right">
                              Bangkok, Thailand
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
